import React from 'react';
import './TotalPortfolioCard.css';

interface TotalPortfolioMetrics {
  total_loans: number;
  current_loans: number;
  past_due_loans: number;
  one_to_10: number;
  eleven_to_30: number;
  thirty_one_to_60: number;
  sixty_one_to_90: number;
  ninety_one_to_120: number;
  one_twenty_one_plus: number;
  thirty_plus: number;
  total_balance: number;
  pct_current: number;
  pct_past_due: number;
  pct_one_to_10: number;
  pct_eleven_to_30: number;
  pct_thirty_one_to_60: number;
  pct_sixty_one_to_90: number;
  pct_ninety_one_to_120: number;
  pct_one_twenty_one_plus: number;
  pct_thirty_plus: number;
  trends?: {
    oneTo10: 'green' | 'orange' | 'red';
    elevenTo30: 'green' | 'orange' | 'red';
    thirtyOneTo60: 'green' | 'orange' | 'red';
    sixtyOneTo90: 'green' | 'orange' | 'red';
    ninetyOneTo120: 'green' | 'orange' | 'red';
    oneTwentyOnePlus: 'green' | 'orange' | 'red';
    thirtyPlus: 'green' | 'orange' | 'red';
  };
}

interface TotalPortfolioCardProps {
  metrics: TotalPortfolioMetrics;
  onViewAllAccounts: () => void;
  onBucketClick: (bucket: string) => void;
  loading?: boolean;
}

const TotalPortfolioCard: React.FC<TotalPortfolioCardProps> = ({
  metrics,
  onViewAllAccounts,
  onBucketClick,
  loading = false
}) => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercent = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  const getHealthColor = (pctPastDue: number): string => {
    if (pctPastDue < 5) return '#10b981'; // green
    if (pctPastDue < 15) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  return (
    <div className="total-portfolio-card">
      <div className="total-portfolio-header">
        <div className="total-portfolio-icon">
          <span className="portfolio-icon">📊</span>
        </div>
        <div className="total-portfolio-info">
          <h3 className="total-portfolio-name">Total Portfolio</h3>
          <p className="total-portfolio-subtitle">All Accounts Combined</p>
        </div>
        <div className="health-indicator">
          <div 
            className="health-dot"
            style={{ backgroundColor: getHealthColor(metrics.pct_past_due) }}
          ></div>
        </div>
      </div>

      <div className="total-portfolio-metrics">
        <div className="metric-row">
          <div className="metric-item">
            <span className="metric-label">Total Loans</span>
            <span className="metric-value">{metrics.total_loans.toLocaleString()}</span>
          </div>
          <div className="metric-item">
            <span className="metric-label">Total Balance</span>
            <span className="metric-value">{formatCurrency(metrics.total_balance)}</span>
          </div>
        </div>

        <div className="metric-row">
          <div className="metric-item">
            <span className="metric-label">Current</span>
            <span className="metric-value current">{formatPercent(metrics.pct_current)}</span>
          </div>
          <div className="metric-item">
            <span className="metric-label">Past Due</span>
            <span className="metric-value past-due">{formatPercent(metrics.pct_past_due)}</span>
          </div>
        </div>

        <div className="past-due-breakdown">
          <div 
            className="breakdown-item clickable"
            onClick={() => onBucketClick('1-10')}
            title="Click to view 1-10 day accounts"
          >
            <span className="breakdown-label">1-10 days</span>
            <span className="breakdown-value">
              {metrics.one_to_10}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.oneTo10}`}
                  title={metrics.trends.oneTo10 === 'green' ? 'Increase < 10%' : 
                         metrics.trends.oneTo10 === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </span>
          </div>
          <div 
            className="breakdown-item clickable"
            onClick={() => onBucketClick('11-30')}
            title="Click to view 11-30 day accounts"
          >
            <span className="breakdown-label">11-30 days</span>
            <span className="breakdown-value">
              {metrics.eleven_to_30}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.elevenTo30}`}
                  title={metrics.trends.elevenTo30 === 'green' ? 'Increase < 10%' : 
                         metrics.trends.elevenTo30 === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </span>
          </div>
          <div 
            className="breakdown-item clickable"
            onClick={() => onBucketClick('31-60')}
            title="Click to view 31-60 day accounts"
          >
            <span className="breakdown-label">31-60 days</span>
            <span className="breakdown-value">
              {metrics.thirty_one_to_60}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.thirtyOneTo60}`}
                  title={metrics.trends.thirtyOneTo60 === 'green' ? 'Increase < 10%' : 
                         metrics.trends.thirtyOneTo60 === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </span>
          </div>
          <div 
            className="breakdown-item clickable"
            onClick={() => onBucketClick('61-90')}
            title="Click to view 61-90 day accounts"
          >
            <span className="breakdown-label">61-90 days</span>
            <span className="breakdown-value">
              {metrics.sixty_one_to_90}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.sixtyOneTo90}`}
                  title={metrics.trends.sixtyOneTo90 === 'green' ? 'Increase < 10%' : 
                         metrics.trends.sixtyOneTo90 === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </span>
          </div>
          <div 
            className="breakdown-item clickable"
            onClick={() => onBucketClick('91-120')}
            title="Click to view 91-120 day accounts"
          >
            <span className="breakdown-label">91-120 days</span>
            <span className="breakdown-value">
              {metrics.ninety_one_to_120}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.ninetyOneTo120}`}
                  title={metrics.trends.ninetyOneTo120 === 'green' ? 'Increase < 10%' : 
                         metrics.trends.ninetyOneTo120 === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </span>
          </div>
          <div 
            className="breakdown-item clickable"
            onClick={() => onBucketClick('120+')}
            title="Click to view 120+ day accounts"
          >
            <span className="breakdown-label">120+ days</span>
            <span className="breakdown-value">
              {metrics.one_twenty_one_plus}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.oneTwentyOnePlus}`}
                  title={metrics.trends.oneTwentyOnePlus === 'green' ? 'Increase < 10%' : 
                         metrics.trends.oneTwentyOnePlus === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </span>
          </div>
          <div 
            className="breakdown-item clickable"
            onClick={() => onBucketClick('30+')}
            title="Click to view 30+ day accounts"
          >
            <span className="breakdown-label">30+ days</span>
            <span className="breakdown-value">
              {metrics.thirty_plus}
              {metrics.trends && (
                <span 
                  className={`trend-indicator ${metrics.trends.thirtyPlus}`}
                  title={metrics.trends.thirtyPlus === 'green' ? 'Increase < 10%' : 
                         metrics.trends.thirtyPlus === 'orange' ? 'Increase 10-20%' : 'Increase ≥ 20%'}
                ></span>
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="total-portfolio-actions">
        <button 
          className="view-all-accounts-btn"
          onClick={onViewAllAccounts}
        >
          View All Accounts
        </button>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="click-hint">
        <span>Click delinquency buckets to view accounts</span>
      </div>
    </div>
  );
};

export default TotalPortfolioCard;
